type Callback<T = unknown> = (args: T) => void;

interface EventCallbacksPair<T = unknown> {
    eventType: string;
    callbacks: Callback<T>[];
}

/**
 * EventBus
 *
 * This class provides a centralized event handling mechanism for facilitating
 * communication between unrelated Stimulus controllers or modules in the application.
 *
 * By decoupling event handling from the DOM, the EventBus allows for cleaner
 * and more maintainable communication patterns, reducing the reliance on custom
 * DOM events or direct references between controllers.
 *
 * Integration with Stimulus:
 * The EventBus is injected into the main Stimulus application and can be accessed
 * within controllers via `this.application.eventBus`.
 *
 * Key Features:
 * - Subscribe to custom events with associated callback functions.
 * - Post events to notify all subscribers with relevant data.
 * - Unsubscribe from specific events to avoid memory leaks or unnecessary processing.
 *
 * Usage Example (in a Stimulus controller):
 *
 * // Subscribing to an event
 * this.application.eventBus.subscribe('custom:event', (data) => {
 *     console.log('Event received:', data);
 * });
 *
 * // Posting an event
 * this.application.eventBus.post('custom:event', { message: 'Hello, world!' });
 *
 * // Unsubscribing from an event
 * const callback = (data) => console.log('Event received:', data);
 * this.application.eventBus.subscribe('custom:event', callback);
 * this.application.eventBus.unsubscribe('custom:event', callback);
 */
export class EventBus {
    private eventCallbacksPairs: EventCallbacksPair[] = [];

    subscribe<T = unknown>(eventType: string, callback: Callback<T>): void {
        const eventCallbacksPair = this.findEventCallbacksPair(eventType);

        if (eventCallbacksPair) {
            eventCallbacksPair.callbacks.push(callback);
        } else {
            this.eventCallbacksPairs.push({
                eventType,
                callbacks: [callback],
            });
        }
    }

    unsubscribe<T = unknown>(eventType: string, callback: Callback<T>): void {
        const eventCallbacksPair = this.findEventCallbacksPair(eventType);

        if (eventCallbacksPair) {
            eventCallbacksPair.callbacks = eventCallbacksPair.callbacks.filter(cb => cb !== callback);

            if (eventCallbacksPair.callbacks.length === 0) {
                this.eventCallbacksPairs = this.eventCallbacksPairs.filter(pair => pair !== eventCallbacksPair);
            }
        }
    }

    /**
     * Post an event to be handled by a registered subscriber
     * console.error statement left in purposefully for debug/testing
     */
    post<T = unknown>(eventType: string, args: T): void {
        const eventCallbacksPair = this.findEventCallbacksPair(eventType);
        if (eventCallbacksPair) {
            eventCallbacksPair.callbacks.forEach(callback => {
                try {
                    callback(args);
                } catch(_error) {
                    // eslint-disable-next-line no-console
                    console.error(`Error in EventBus callback for event "${eventType}":`, _error);
                }
            });
        }
    }

    private findEventCallbacksPair(eventType: string): EventCallbacksPair | undefined {
        return this.eventCallbacksPairs.find(pair => pair.eventType === eventType);
    }
}

const eventBus = new EventBus();
Object.freeze(eventBus);

export default eventBus;
